import React, { useEffect, useState } from 'react';

interface ScreenInfo {
    width: number;
    height: number;
    devicePixelRatio: number;
    isRetina: boolean;
    isMacBookRetina: boolean;
    isHighResLaptop: boolean;
    resolution: string;
    deviceType: 'mobile' | 'tablet' | 'laptop' | 'desktop' | 'ultrawide' | '4k';
}

interface ResolutionWrapperProps {
    children: React.ReactNode;
    enableScaling?: boolean;
    enableFontScaling?: boolean;
    enableSpacingAdjustments?: boolean;
    className?: string;
}

const ResolutionWrapper: React.FC<ResolutionWrapperProps> = ({
                                                                 children,
                                                                 enableScaling = true,
                                                                 enableFontScaling = true,
                                                                 enableSpacingAdjustments = true,
                                                                 className = ''
                                                             }) => {
    const [screenInfo, setScreenInfo] = useState<ScreenInfo>({
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080,
        devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
        isRetina: typeof window !== 'undefined' ? window.devicePixelRatio > 1.5 : false,
        isMacBookRetina: false,
        isHighResLaptop: false,
        resolution: '1920x1080',
        deviceType: 'desktop'
    });

    useEffect(() => {
        const updateScreenInfo = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const devicePixelRatio = window.devicePixelRatio;
            const isRetina = devicePixelRatio > 1.5;

            // Detect MacBook Retina displays (common MacBook resolutions)
            const isMacBookRetina = isRetina && (
                (width >= 1440 && width <= 1680 && height >= 900 && height <= 1050) || // MacBook Pro 14", 16"
                (width >= 1280 && width <= 1440 && height >= 800 && height <= 900)     // MacBook Air 13"
            );

            // Detect other high-resolution laptops (Dell XPS, Surface, ThinkPad X1, etc.)
            const isHighResLaptop = !isMacBookRetina && (
                // High-res Windows laptops, Linux laptops, etc.
                (width >= 1440 && width <= 1920 && height >= 900 && height <= 1200) ||
                // Surface Pro/Laptop, Dell XPS 13/15, ThinkPad X1
                (width >= 1366 && width <= 1600 && height >= 768 && height <= 1024 && devicePixelRatio > 1) ||
                // Gaming laptops with high DPI
                (width >= 1920 && width <= 2560 && height >= 1080 && height <= 1600 && devicePixelRatio > 1.2)
            );

            // Determine device type
            let deviceType: ScreenInfo['deviceType'] = 'desktop';
            if (width < 768) deviceType = 'mobile';
            else if (width < 1024) deviceType = 'tablet';
            else if (width < 1920) deviceType = 'laptop';
            else if (width < 2560) deviceType = 'desktop';
            else if (width >= 3440) deviceType = 'ultrawide';
            else deviceType = '4k';

            const newScreenInfo: ScreenInfo = {
                width,
                height,
                devicePixelRatio,
                isRetina,
                isMacBookRetina,
                isHighResLaptop,
                resolution: `${window.screen.width}x${window.screen.height}`,
                deviceType
            };
            setScreenInfo(newScreenInfo);
        };

        updateScreenInfo();

        // Debounced resize handler
        let timeoutId: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateScreenInfo, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, []);

    // Generate CSS classes based on current screen info
    const getResolutionClasses = (): string => {
        const classes = ['resolution-wrapper'];

        if (enableScaling) {
            // Add device-specific classes for CSS targeting
            if (screenInfo.isMacBookRetina) {
                classes.push('macbook-retina');
            } else if (screenInfo.isHighResLaptop) {
                classes.push('high-res-laptop');
            } else {
                // Standard resolution classifications
                if (screenInfo.width <= 1024) classes.push('res-small');
                else if (screenInfo.width <= 1366) classes.push('res-medium');
                else if (screenInfo.width <= 1920) classes.push('res-large');
                else if (screenInfo.width <= 2560) classes.push('res-xl');
                else classes.push('res-4k');
            }

            if (screenInfo.isRetina) classes.push('retina-display');
            classes.push(`device-${screenInfo.deviceType}`);
        }

        if (enableFontScaling) classes.push('resolution-font-scaling');
        if (enableSpacingAdjustments) classes.push('resolution-spacing');

        return classes.join(' ');
    };

    // Calculate dynamic scaling factor with support for all high-res laptops
    const getScalingFactor = (): number => {
        if (!enableScaling) return 1;

        const { width, height, isMacBookRetina, isHighResLaptop, devicePixelRatio } = screenInfo;

        // Never scale MacBook Retina or other high-res laptops that work well
        if (isMacBookRetina || isHighResLaptop) return 1;

        // Scale based on resolution and pixel density
        if (width <= 1024 && height <= 768) {
            // Old small laptops
            return 0.85;
        } else if (width <= 1366 && height <= 768) {
            // Common budget laptop resolution
            return 0.9;
        } else if (width >= 3440) {
            // Ultrawide monitors
            return 1.1;
        } else if (width >= 2560) {
            // 4K and high-res displays
            return devicePixelRatio > 1 ? 1.2 : 1.3;
        }

        return 1; // No scaling for standard resolutions
    };

    // Dynamic styles for fine-tuning
    const getDynamicStyles = (): React.CSSProperties & { '--resolution-viewport-height': string } => {
        const scalingFactor = getScalingFactor();
        const { isMacBookRetina, isHighResLaptop } = screenInfo;

        const baseStyles: React.CSSProperties & { '--resolution-viewport-height': string } = {
            '--resolution-viewport-height': '100vh',
            // Smooth transitions
            transition: 'transform 0.3s ease',
            // Ensure full height on high-res laptops
            minHeight: '100vh',
            height: (isMacBookRetina || isHighResLaptop) ? 'auto' : '100vh',
        };

        if (scalingFactor !== 1 && enableScaling) {
            return {
                ...baseStyles,
                transform: `scale(${scalingFactor})`,
                transformOrigin: 'top left',
                width: `${100 / scalingFactor}%`,
                '--resolution-viewport-height': `${100 / scalingFactor}vh`,
                height: `${100 / scalingFactor}vh`,
                minHeight: `${100 / scalingFactor}vh`,
                overflow: 'hidden'
            };
        }

        return baseStyles;
    };

    // Additional body styles for high-resolution laptops
    useEffect(() => {
        if (screenInfo.isMacBookRetina || screenInfo.isHighResLaptop) {
            // Ensure body and html are properly sized for high-res laptops
            document.documentElement.style.height = 'auto';
            document.documentElement.style.minHeight = '100vh';
            document.documentElement.style.overflow = 'auto';
            document.body.style.height = 'auto';
            document.body.style.minHeight = '100vh';
            document.body.style.overflow = 'auto';

            // Improve font rendering on high-DPI displays
            (document.body.style as any).webkitFontSmoothing = 'antialiased';
            (document.body.style as any).mozOsxFontSmoothing = 'grayscale';
        }

        return () => {
            // Cleanup
            document.documentElement.style.height = '';
            document.documentElement.style.minHeight = '';
            document.documentElement.style.overflow = '';
            document.body.style.height = '';
            document.body.style.minHeight = '';
            document.body.style.overflow = '';
            (document.body.style as any).webkitFontSmoothing = '';
            (document.body.style as any).mozOsxFontSmoothing = '';
        };
    }, [screenInfo.isMacBookRetina, screenInfo.isHighResLaptop]);

    return (
        <div
            className={`${getResolutionClasses()} ${className}`.trim()}
            style={getDynamicStyles()}
            data-resolution={screenInfo.resolution}
            data-pixel-ratio={screenInfo.devicePixelRatio}
            data-retina={screenInfo.isRetina}
            data-macbook-retina={screenInfo.isMacBookRetina}
            data-high-res-laptop={screenInfo.isHighResLaptop}
            data-device-type={screenInfo.deviceType}
        >
            {children}
        </div>
    );
};

export default ResolutionWrapper;

// Hook for getting current resolution info
export const useResolutionInfo = () => {
    const [screenInfo, setScreenInfo] = useState<ScreenInfo>({
        width: typeof window !== 'undefined' ? window.innerWidth : 1920,
        height: typeof window !== 'undefined' ? window.innerHeight : 1080,
        devicePixelRatio: typeof window !== 'undefined' ? window.devicePixelRatio : 1,
        isRetina: typeof window !== 'undefined' ? window.devicePixelRatio > 1.5 : false,
        isMacBookRetina: false,
        isHighResLaptop: false,
        resolution: '1920x1080',
        deviceType: 'desktop'
    });

    useEffect(() => {
        const updateScreenInfo = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            const devicePixelRatio = window.devicePixelRatio;
            const isRetina = devicePixelRatio > 1.5;

            // Detect MacBook Retina displays
            const isMacBookRetina = isRetina && (
                (width >= 1440 && width <= 1680 && height >= 900 && height <= 1050) ||
                (width >= 1280 && width <= 1440 && height >= 800 && height <= 900)
            );

            // Detect other high-resolution laptops
            const isHighResLaptop = !isMacBookRetina && (
                (width >= 1440 && width <= 1920 && height >= 900 && height <= 1200) ||
                (width >= 1366 && width <= 1600 && height >= 768 && height <= 1024 && devicePixelRatio > 1) ||
                (width >= 1920 && width <= 2560 && height >= 1080 && height <= 1600 && devicePixelRatio > 1.2)
            );

            // Determine device type
            let deviceType: ScreenInfo['deviceType'] = 'desktop';
            if (width < 768) deviceType = 'mobile';
            else if (width < 1024) deviceType = 'tablet';
            else if (width < 1920) deviceType = 'laptop';
            else if (width < 2560) deviceType = 'desktop';
            else if (width >= 3440) deviceType = 'ultrawide';
            else deviceType = '4k';

            setScreenInfo({
                width,
                height,
                devicePixelRatio,
                isRetina,
                isMacBookRetina,
                isHighResLaptop,
                resolution: `${window.screen.width}x${window.screen.height}`,
                deviceType
            });
        };

        updateScreenInfo();

        let timeoutId: NodeJS.Timeout;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(updateScreenInfo, 150);
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, []);

    return {
        ...screenInfo,
        isSmallScreen: screenInfo.width <= 1366,
        isLargeScreen: screenInfo.width >= 1920,
        is4K: screenInfo.width >= 2560,
        needsScaling: !(screenInfo.isMacBookRetina || screenInfo.isHighResLaptop),
        getScaleRecommendation: () => {
            if (screenInfo.isMacBookRetina || screenInfo.isHighResLaptop) return 1;
            if (screenInfo.width <= 1024) return 0.85;
            if (screenInfo.width <= 1366) return 0.9;
            if (screenInfo.width >= 2560) return 1.2;
            return 1;
        },
        getOptimalSettings: () => {
            return {
                enableScaling: !(screenInfo.isMacBookRetina || screenInfo.isHighResLaptop),
                enableFontScaling: true,
                enableSpacingAdjustments: screenInfo.width <= 1366
            };
        }
    };
};
